import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Ng1ListenerService } from '../ng1-listener/ng1-listener.service';

import { IUser, UserPlan, IUserPlan } from '@app/core/interfaces';
import { ApiService } from '../api/api.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly _user = new BehaviorSubject<IUser | undefined>(undefined);
    readonly user$ = this._user.asObservable();

    /** Stores basic data about the user's CalendarHero plan */
    private readonly _userPlan = new BehaviorSubject<IUserPlan>(null);
    readonly userPlan$ = this._userPlan.asObservable();

    public isPlatformUser = false; // if the user is SSO'ing into the app from V Platform

    constructor(
        @Inject('authService') public authService,
        private ng1ListenerService: Ng1ListenerService,
        private apiService: ApiService
    ) {
        this.getUser();
        this.ng1ListenerService.onUserUpdate$.subscribe((user?: IUser) => {
            if (user) {
                this.updateUser(user, false);
            }
        });
    }

    get user() {
        return this._user.getValue();
    }

    set user(user) {
        this._user.next(user);
    }

    /**
     * authService.setUser()
     * @params user: the user object
     * @params shouldUpdateUserObject if true, will call updateUser() in settings.js
     * */
    updateUser(user: IUser, shouldBroadcastToNg1: boolean = true, shouldUpdateUserObject = true) {
        this.isPlatformUser = Boolean(user.vendastaUserId) || Boolean(user.vendastaLegacyUserId);
        this.user = user;
        this.updateUserPlan(user);
        if (shouldBroadcastToNg1) {
            this.authService.setUser(user, shouldUpdateUserObject);
        }
    }

    getUser() {
        new Observable((observer) => {
            this.fetchUserFromApi(observer);
        }).subscribe((user: IUser) => {
            this.updateUser(user);
        });
    }

    fetchUserFromApi(observer) {
        const user = this.authService.get();
        if (!user.meeting || !user.meeting.defaultType || !user.meeting.types) {
            setTimeout(() => this.fetchUserFromApi(observer), 0);
            return;
        }
        observer.next(user);
        observer.complete();
    }

    get userPlan() {
        return this._userPlan.getValue();
    }

    set userPlan(userPlan: IUserPlan) {
        this._userPlan.next(userPlan);
    }

    updateUserPlan(user: IUser) {
        const _userPlan = this.getUserPlan(user);
        this.userPlan = _userPlan;
    }

    public getUserPlan(user: IUser) {
        return {
            isBasicUser: user.plan === UserPlan.Basic,
            isCorporateUser: user.plan === UserPlan.Enterprise || user.plan === UserPlan.Corporate,
            isProfessionalUser: user.plan === UserPlan.Professional || user.plan === UserPlan.Trial,
            isTrialUser: user.plan === UserPlan.Trial,
            isBetaUser: user.versions && !!user.versions.beta
        };
    }
}
